import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack,
  Policy as PolicyIcon,
  AttachMoney,
  Schedule,
  CheckCircle,
  Warning,
  Agriculture,
  Water,
  Description,
  Assignment,
  Refresh,
  Download,
  Print,
  Share,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { policiesAPI, claimsAPI, documentsAPI } from 'services/api';
import DOCProgressIndicator from 'shared/components/DOCProgressIndicator';

const PolicyDetailView = () => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [claims, setClaims] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadPolicyDetails();
  }, [policyId]);

  const loadPolicyDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load policy details
      const policyResponse = await policiesAPI.getById(policyId);
      const policyData = policyResponse.data;
      
      // Load related claims
      const claimsResponse = await claimsAPI.getByPolicy(policyId);
      
      // Load related documents
      const documentsResponse = await documentsAPI.getByPolicy(policyId);

      setPolicy(policyData);
      setClaims(claimsResponse.data || []);
      setDocuments(documentsResponse.data || []);

    } catch (err) {
      console.error('Error loading policy details:', err);
      setError('Failed to load policy details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: 'warning', label: 'Pending Approval', icon: <Schedule /> },
      active: { color: 'success', label: 'Active', icon: <CheckCircle /> },
      expired: { color: 'default', label: 'Expired', icon: <Warning /> },
      cancelled: { color: 'error', label: 'Cancelled', icon: <Warning /> }
    };
    return configs[status] || { color: 'default', label: status, icon: null };
  };

  const StatusChip = ({ status }) => {
    const config = getStatusConfig(status);
    return <Chip label={config.label} color={config.color} size="medium" icon={config.icon} />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const calculateDaysRemaining = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={loadPolicyDetails}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!policy) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          Policy not found.
        </Alert>
      </Container>
    );
  }

  const daysRemaining = calculateDaysRemaining(policy.policy_end_date);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/farmer/policies')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box flexGrow={1}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Policy Details
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Policy #{policy.policy_number || policy.id}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button startIcon={<Print />} variant="outlined">
            Print
          </Button>
          <Button startIcon={<Download />} variant="outlined">
            Download
          </Button>
          <Button startIcon={<Share />} variant="outlined">
            Share
          </Button>
        </Box>
      </Box>

      {/* Status Banner */}
      {daysRemaining <= 30 && daysRemaining > 0 && policy.status === 'active' && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" startIcon={<Refresh />}>
              Renew Policy
            </Button>
          }
        >
          Your policy expires in {daysRemaining} days. Consider renewing to ensure continuous coverage.
        </Alert>
      )}

      {/* Main Policy Information */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <PolicyIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {policy.plan} Insurance Plan
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {policy.species} Aquaculture Coverage
                </Typography>
              </Box>
              <Box ml="auto">
                <StatusChip status={policy.status} />
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Policy Number
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {policy.policy_number || `#${policy.id}`}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Species
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                  {policy.species}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Policy Start Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(policy.policy_start_date).toLocaleDateString()}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Policy End Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(policy.policy_end_date).toLocaleDateString()}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {formatCurrency(policy.sum_insured)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sum Insured
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {formatCurrency(policy.final_premium)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Premium
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color={daysRemaining <= 7 ? 'error.main' : 'text.primary'} fontWeight="bold">
                    {daysRemaining > 0 ? daysRemaining : 'Expired'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Days Remaining
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* DOC Progress Indicator */}
            {policy.policy_start_date && policy.policy_end_date && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Days of Culture (DOC) Progress
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <DOCProgressIndicator
                    stockingDate={policy.policy_start_date}
                    targetDOC={(() => {
                      try {
                        const startDate = new Date(policy.policy_start_date);
                        const endDate = new Date(policy.policy_end_date);
                        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                          return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                        }
                      } catch (e) {
                        console.warn('Error calculating DOC:', e);
                      }
                      return 120; // Default fallback
                    })()}
                    variant="card"
                    size="large"
                    showDetails={true}
                    label="DOC Progress"
                  />
                </Box>
              </Grid>
            )}

            {policy.notes && (
              <Box mt={3}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body1">
                  {policy.notes}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Premium Breakdown
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Base Premium" 
                  secondary={formatCurrency(policy.premium_amount)} 
                />
              </ListItem>
              {policy.discount_applied > 0 && (
                <ListItem>
                  <ListItemText 
                    primary="Discount Applied" 
                    secondary={`-${policy.discount_applied}%`}
                  />
                </ListItem>
              )}
              {policy.loading_applied > 0 && (
                <ListItem>
                  <ListItemText 
                    primary="Loading Applied" 
                    secondary={`+${policy.loading_applied}%`}
                  />
                </ListItem>
              )}
              <Divider />
              <ListItem>
                <ListItemText 
                  primary="Final Premium" 
                  secondary={formatCurrency(policy.final_premium)}
                  primaryTypographyProps={{ fontWeight: 'bold' }}
                  secondaryTypographyProps={{ fontWeight: 'bold', color: 'primary.main' }}
                />
              </ListItem>
            </List>
          </Paper>

          {/* Farm and Pond Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Coverage Details
            </Typography>
            <Box display="flex" alignItems="center" mb={2}>
              <Agriculture sx={{ mr: 1, color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Farm
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {policy.farm_name || 'Farm Information'}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center">
              <Water sx={{ mr: 1, color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Pond
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {policy.pond_name || 'Pond Information'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs for Claims and Documents */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={`Claims (${claims.length})`} icon={<Assignment />} />
          <Tab label={`Documents (${documents.length})`} icon={<Description />} />
          <Tab label="Policy History" icon={<Schedule />} />
        </Tabs>

        {/* Claims Tab */}
        {tabValue === 0 && (
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Policy Claims
            </Typography>
            {claims.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Claim Number</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Date Filed</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {claims.map((claim) => (
                      <TableRow key={claim.id}>
                        <TableCell>{claim.claim_number}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{claim.claim_type}</TableCell>
                        <TableCell>{new Date(claim.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{formatCurrency(claim.estimated_loss)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={claim.status} 
                            color={claim.status === 'approved' ? 'success' : claim.status === 'rejected' ? 'error' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => navigate(`/farmer/claims/${claim.id}`)}>
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <Assignment sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No Claims Filed
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  You haven't filed any claims for this policy yet.
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/farmer/claims')}
                  sx={{ mt: 2 }}
                >
                  File a Claim
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Documents Tab */}
        {tabValue === 1 && (
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Policy Documents
            </Typography>
            {documents.length > 0 ? (
              <List>
                {documents.map((document) => (
                  <ListItem key={document.id} divider>
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText
                      primary={document.title}
                      secondary={`Uploaded: ${new Date(document.created_at).toLocaleDateString()}`}
                    />
                    <Chip 
                      label={document.status} 
                      color={document.status === 'verified' ? 'success' : document.status === 'rejected' ? 'error' : 'warning'}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Button size="small" startIcon={<Download />}>
                      Download
                    </Button>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box textAlign="center" py={4}>
                <Description sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No Documents Uploaded
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload supporting documents for this policy.
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/farmer/documents')}
                  sx={{ mt: 2 }}
                >
                  Upload Documents
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Policy History Tab */}
        {tabValue === 2 && (
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Policy History & Timeline
            </Typography>
            
            {/* Professional Timeline using Cards */}
            <Box sx={{ position: 'relative', '&:before': { content: '""', position: 'absolute', left: '20px', top: 0, bottom: 0, width: '2px', backgroundColor: 'primary.main' } }}>
              
              {/* Policy Created Event */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, position: 'relative' }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  backgroundColor: 'primary.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  zIndex: 1,
                  mr: 3
                }}>
                  <PolicyIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Card sx={{ flexGrow: 1, boxShadow: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6" color="primary">
                        Policy Created
                      </Typography>
                      <Chip label={new Date(policy.created_at).toLocaleDateString()} size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Policy application submitted and entered into the system
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Policy Approved Event */}
              {policy.approved_at && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, position: 'relative' }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    backgroundColor: 'success.main', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    zIndex: 1,
                    mr: 3
                  }}>
                    <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Card sx={{ flexGrow: 1, boxShadow: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" color="success.main">
                          Policy Approved & Activated
                        </Typography>
                        <Chip label={new Date(policy.approved_at).toLocaleDateString()} color="success" size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Policy approved by admin and coverage became active
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Claims Events */}
              {claims.map((claim, index) => (
                <Box key={claim.id} sx={{ display: 'flex', alignItems: 'center', mb: 3, position: 'relative' }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    backgroundColor: 'warning.main', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    zIndex: 1,
                    mr: 3
                  }}>
                    <Assignment sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Card sx={{ flexGrow: 1, boxShadow: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" color="warning.main">
                          Claim Filed - {claim.claim_type}
                        </Typography>
                        <Chip label={new Date(claim.created_at).toLocaleDateString()} color="warning" size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Claim for {formatCurrency(claim.estimated_loss)} submitted for review
                      </Typography>
                      <Box mt={1}>
                        <Chip 
                          label={claim.status} 
                          color={claim.status === 'approved' ? 'success' : claim.status === 'rejected' ? 'error' : 'default'}
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))}

              {/* Policy Expiry Event */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, position: 'relative' }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  backgroundColor: daysRemaining > 0 ? 'info.main' : 'grey.500', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  zIndex: 1,
                  mr: 3
                }}>
                  <Schedule sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Card sx={{ flexGrow: 1, boxShadow: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6" color={daysRemaining > 0 ? 'info.main' : 'text.secondary'}>
                        Policy {daysRemaining > 0 ? 'Expires' : 'Expired'}
                      </Typography>
                      <Chip 
                        label={new Date(policy.policy_end_date).toLocaleDateString()} 
                        color={daysRemaining > 0 ? 'info' : 'default'} 
                        size="small" 
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Coverage {daysRemaining > 0 ? 'ends' : 'ended'} on this date
                    </Typography>
                    {daysRemaining > 0 && daysRemaining <= 30 && (
                      <Box mt={1}>
                        <Chip 
                          label={`${daysRemaining} days remaining`} 
                          color="warning" 
                          size="small"
                          icon={<Warning />}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>

              {/* Policy Statistics Summary */}
              <Card sx={{ mt: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Policy Summary Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="primary">
                          {Math.ceil((new Date(policy.policy_end_date) - new Date(policy.policy_start_date)) / (1000 * 60 * 60 * 24))}
                        </Typography>
                        <Typography variant="caption">Total Days</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="success.main">
                          {claims.length}
                        </Typography>
                        <Typography variant="caption">Claims Filed</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="info.main">
                          {documents.length}
                        </Typography>
                        <Typography variant="caption">Documents</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="warning.main">
                          {((claims.length / Math.max(1, Math.ceil((new Date() - new Date(policy.policy_start_date)) / (1000 * 60 * 60 * 24 * 30)))) * 100).toFixed(2)}%
                        </Typography>
                        <Typography variant="caption">Monthly Claim Rate</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default PolicyDetailView;
